import { BrandLogo, Button } from '@/shared/ui';
import { useState } from 'react';
import { getAppConfig } from '../../config/appConfigRef';
import { getAccountAppUrl } from '../../config/getElvaLoginUrl';
import { useAuthStore } from '../authStore';

export interface AppAccessLockedScreenProps {
  logoSrc?: string;
}

export function AppAccessLockedScreen({ logoSrc }: AppAccessLockedScreenProps) {
  const appName = getAppConfig().appName;
  const blockReason = useAuthStore((s) => s.blockReason);
  const locations = useAuthStore((s) => s.locations);
  const user = useAuthStore((s) => s.user);
  const switchActiveLocation = useAuthStore((s) => s.switchActiveLocation);
  const logout = useAuthStore((s) => s.logout);
  const [pendingLocationId, setPendingLocationId] = useState<string | null>(null);

  const copy = {
    organization_account: {
      title: 'Organization accounts cannot open Elva apps',
      body: `This is an organization account. It manages people, plans and credits in the Elva account app, but it holds no membership at a location, so it cannot open ${appName}. Sign in with a personal account instead.`,
    },
    app_not_permitted: {
      title: `You do not have ${appName} here`,
      body: `Your account does not include ${appName} at this location. An administrator can grant it in the Elva account app. If you work at more than one location, try switching below.`,
    },
    location_required: {
      title: 'Choose a location to continue',
      body: `Your role and permissions belong to a location, so ${appName} needs to know where you are working.`,
    },
  }[blockReason ?? 'app_not_permitted'];

  const handleSwitch = async (locationId: string) => {
    setPendingLocationId(locationId);
    try {
      await switchActiveLocation(locationId);
    } catch (error) {
      console.error('Failed to switch location:', error);
    } finally {
      setPendingLocationId(null);
    }
  };

  const showPicker = blockReason !== 'organization_account' && locations.length > 0;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--cream-0)] px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center">
          {logoSrc ? (
            <BrandLogo src={logoSrc} alt={appName} className="h-14 w-14" />
          ) : (
            <div className="h-14 w-14 rounded-2xl bg-[var(--grad-spectrum)]" />
          )}
        </div>

        <div className="space-y-2 text-center">
          <h1 className="font-[var(--font-display)] text-2xl font-semibold text-[var(--ink-0)]">
            {copy.title}
          </h1>
          <p className="text-sm text-[var(--ink-2)]">{copy.body}</p>
          {user?.email ? (
            <p className="text-xs text-[var(--ink-3)]">Signed in as {user.email}</p>
          ) : null}
        </div>

        {showPicker ? (
          <ul className="divide-y divide-[var(--cream-2)] overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)]">
            {locations.map((location) => (
              <li key={location.id}>
                <button
                  type="button"
                  disabled={pendingLocationId !== null}
                  onClick={() => {
                    void handleSwitch(location.id);
                  }}
                  className="flex w-full flex-col px-4 py-3 text-left hover:bg-[var(--cream-1)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="truncate text-sm font-semibold text-[var(--ink-0)]">
                    {location.name}
                  </span>
                  <span className="truncate text-xs text-[var(--ink-3)]">
                    {pendingLocationId === location.id
                      ? 'Switching…'
                      : (location.ascend_id ?? '—')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex flex-col items-center gap-2">
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="w-full max-w-[280px]"
            onClick={() => {
              window.location.href = getAccountAppUrl('/');
            }}
          >
            Go to Elva Account
          </Button>
          <button
            type="button"
            className="text-xs text-[var(--ink-3)] underline"
            onClick={() => {
              void logout();
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
